'use client';

import React, { useState } from 'react';
import { X, ChevronRight, ChevronLeft, AlignLeft } from 'lucide-react';
import type { AppraisalTemplate, TemplateSection, Question } from '@/store/appraisalStore';

interface Props {
  template: AppraisalTemplate;
  onClose: () => void;
}

function QuestionPreview({ question }: { question: Question }) {
  const [ratingVal, setRatingVal] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [textVal, setTextVal] = useState('');

  const toggleOption = (opt: string) => {
    if (['single-select', 'radio', 'dropdown'].includes(question.type)) {
      setSelectedOptions([opt]);
    } else {
      setSelectedOptions((prev) =>
        prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]
      );
    }
  };

  return (
    <div style={{
      background: 'var(--pn-surface)',
      border: '1px solid var(--pn-border)',
      borderRadius: 'var(--pn-radius-md)',
      padding: 16,
      marginBottom: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--pn-text-primary)', lineHeight: 1.4 }}>
            {question.text}
            {question.required && <span style={{ color: 'var(--pn-danger)', marginLeft: 4 }}>*</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 12 }}>
          {question.weightage > 0 && (
            <span style={{ fontSize: 10.5, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 4 }}>
              {question.weightage}%
            </span>
          )}
          <span style={{ fontSize: 10.5, fontWeight: 600, background: 'var(--pn-primary-light)', color: 'var(--pn-primary)', padding: '2px 7px', borderRadius: 4 }}>
            {question.type.replace('-', ' ')}
          </span>
        </div>
      </div>

      {/* Rating */}
      {['rating', 'competency'].includes(question.type) && (
        <div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {Array.from({ length: (question.maxRating || 5) - (question.minRating || 1) + 1 }, (_, i) => (question.minRating || 1) + i).map((n) => (
              <button
                key={`rating-${question.id}-${n}`}
                type="button"
                onClick={() => setRatingVal(n)}
                style={{
                  width: 40, height: 40, borderRadius: 'var(--pn-radius)',
                  border: `2px solid ${ratingVal === n ? 'var(--pn-primary)' : 'var(--pn-border)'}`,
                  background: ratingVal === n ? 'var(--pn-primary)' : 'var(--pn-surface)',
                  color: ratingVal === n ? 'white' : 'var(--pn-text-secondary)',
                  fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  fontFamily: 'var(--pn-font-sans)', transition: 'all var(--pn-transition)',
                }}
                aria-label={`Rating ${n}`}
                aria-pressed={ratingVal === n}
              >
                {n}
              </button>
            ))}
          </div>
          {ratingVal > 0 && (
            <div style={{ fontSize: 12, color: 'var(--pn-primary)', marginTop: 6, fontWeight: 600 }}>
              Selected: {ratingVal} / {question.maxRating || 5}
            </div>
          )}
        </div>
      )}

      {/* Text / Textarea */}
      {['text', 'goal', 'kpi'].includes(question.type) && (
        <div>
          <input
            type="text"
            className="pn-appraisal__form-input"
            placeholder="Type your answer here…"
            value={textVal}
            onChange={(e) => setTextVal(e.target.value)}
            maxLength={question.maxChars || undefined}
          />
          {question.maxChars && (
            <div style={{ fontSize: 11, color: 'var(--pn-text-muted)', textAlign: 'right', marginTop: 3 }}>
              {textVal.length} / {question.maxChars}
            </div>
          )}
        </div>
      )}

      {question.type === 'textarea' && (
        <div>
          <textarea
            className="pn-appraisal__form-textarea"
            placeholder="Type your detailed response here…"
            value={textVal}
            onChange={(e) => setTextVal(e.target.value)}
            rows={3}
            maxLength={question.maxChars || undefined}
          />
          {question.maxChars && (
            <div style={{ fontSize: 11, color: 'var(--pn-text-muted)', textAlign: 'right', marginTop: 3 }}>
              {textVal.length} / {question.maxChars}
            </div>
          )}
        </div>
      )}

      {/* Select / Radio / Checkbox / Dropdown */}
      {['single-select', 'multi-select', 'radio', 'checkbox'].includes(question.type) && question.options && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          {question.options.map((opt, idx) => {
            const isSelected = selectedOptions.includes(opt);
            const isMulti = ['multi-select', 'checkbox'].includes(question.type);
            return (
              <label
                key={`preview-option-${question.id}-${idx}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                  padding: '8px 12px', borderRadius: 'var(--pn-radius)',
                  border: `1.5px solid ${isSelected ? 'var(--pn-primary)' : 'var(--pn-border)'}`,
                  background: isSelected ? 'var(--pn-primary-light)' : 'var(--pn-surface)',
                  transition: 'all var(--pn-transition)',
                  fontSize: 13,
                }}
              >
                <div style={{
                  width: 16, height: 16, flexShrink: 0,
                  borderRadius: isMulti ? 3 : '50%',
                  border: `2px solid ${isSelected ? 'var(--pn-primary)' : 'var(--pn-border-strong)'}`,
                  background: isSelected ? 'var(--pn-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isSelected && <div style={{ width: 6, height: 6, borderRadius: isMulti ? 1 : '50%', background: 'white' }} />}
                </div>
                <input
                  type={isMulti ? 'checkbox' : 'radio'}
                  checked={isSelected}
                  onChange={() => toggleOption(opt)}
                  style={{ display: 'none' }}
                  name={`q-${question.id}`}
                />
                {opt}
              </label>
            );
          })}
        </div>
      )}

      {question.type === 'dropdown' && question.options && (
        <select
          className="pn-appraisal__form-select"
          value={selectedOptions[0] || ''}
          onChange={(e) => setSelectedOptions([e.target.value])}
          aria-label={question.text}
        >
          <option value="">Select an option…</option>
          {question.options.map((opt, idx) => (
            <option key={`dd-opt-${question.id}-${idx}`} value={opt}>{opt}</option>
          ))}
        </select>
      )}
    </div>
  );
}

function SectionPreview({ section }: { section: TemplateSection }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--pn-primary-light), rgba(124,58,237,0.05))',
        border: '1px solid var(--pn-primary-mid)',
        borderRadius: 'var(--pn-radius-md)',
        padding: '12px 16px',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--pn-primary)' }}>{section.name}</div>
          {section.description && (
            <div style={{ fontSize: 12, color: 'var(--pn-text-secondary)', marginTop: 2 }}>{section.description}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, background: 'white', color: 'var(--pn-primary)', padding: '3px 9px', borderRadius: 20, border: '1px solid var(--pn-primary-mid)' }}>
            {section.weightage}% weight
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--pn-text-secondary)' }}>
            {section.questions.length} questions
          </span>
        </div>
      </div>
      {section.questions.map((q) => (
        <QuestionPreview key={q.id} question={q} />
      ))}
    </div>
  );
}

export default function TemplatePreviewModal({ template, onClose }: Props) {
  const [currentSection, setCurrentSection] = useState(0);
  const section = template.sections[currentSection];
  const totalSections = template.sections.length;

  return (
    <div className="pn-appraisal__modal-overlay" role="dialog" aria-modal="true" aria-labelledby="preview-modal-title">
      <div className="pn-appraisal__modal pn-appraisal__modal--lg">
        <div className="pn-appraisal__modal-header">
          <div>
            <div className="pn-appraisal__modal-header-title" id="preview-modal-title">
              Form Preview — {template.name}
            </div>
            <div className="pn-appraisal__modal-header-subtitle">
              {template.version} · {template.sections.length} sections · {template.sections.reduce((a, s) => a + s.questions.length, 0)} questions
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--pn-text-muted)', fontWeight: 600 }}>
              Preview only — no data is saved
            </span>
            <button className="pn-appraisal__modal-header-close" onClick={onClose} aria-label="Close preview">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Section tabs */}
        {totalSections > 0 && (
          <div style={{ borderBottom: '1px solid var(--pn-border)', padding: '0 24px', overflowX: 'auto' }}>
            <div className="pn-appraisal__tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
              {template.sections.map((s, idx) => (
                <button
                  key={`preview-tab-${s.id}`}
                  className={`pn-appraisal__tabs-item${currentSection === idx ? ' pn-appraisal__tabs-item--active' : ''}`}
                  onClick={() => setCurrentSection(idx)}
                >
                  {idx + 1}. {s.name}
                  <span className="pn-appraisal__tabs-item-count">{s.questions.length}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="pn-appraisal__modal-body">
          {totalSections === 0 ? (
            <div className="pn-appraisal__empty">
              <div className="pn-appraisal__empty-icon">
                <AlignLeft size={26} />
              </div>
              <div className="pn-appraisal__empty-title">No sections in this template</div>
              <p className="pn-appraisal__empty-desc">
                Add sections and questions to the template to see a preview here.
              </p>
            </div>
          ) : section ? (
            <SectionPreview section={section} />
          ) : null}
        </div>

        <div className="pn-appraisal__modal-footer pn-appraisal__modal-footer--between">
          <button
            className="pn-appraisal__btn pn-appraisal__btn--secondary"
            onClick={() => setCurrentSection((s) => Math.max(0, s - 1))}
            disabled={currentSection === 0}
          >
            <ChevronLeft size={14} /> Previous Section
          </button>
          <span style={{ fontSize: 12.5, color: 'var(--pn-text-muted)', fontWeight: 600 }}>
            Section {currentSection + 1} of {totalSections}
          </span>
          {currentSection < totalSections - 1 ? (
            <button
              className="pn-appraisal__btn pn-appraisal__btn--primary"
              onClick={() => setCurrentSection((s) => Math.min(totalSections - 1, s + 1))}
            >
              Next Section <ChevronRight size={14} />
            </button>
          ) : (
            <button className="pn-appraisal__btn pn-appraisal__btn--success" onClick={onClose}>
              Done Previewing
            </button>
          )}
        </div>
      </div>
    </div>
  );
}